/**
 * Login screen — password + passkey, composed by LoginPanel (plan §6.8.11 · AD-9).
 */
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback } from 'react';

import { LoginPanel } from '@/auth/login-panel';
import { readReturnToState } from '@/auth/return-to';
import { loadConfig } from '@/config';

import styles from './login-route.module.css';
import { usePostLoginNavigation } from './use-post-login-navigation';

import type { JSX } from 'react';

const config = loadConfig();

export function LoginRoute(): JSX.Element {
  const navigate = useNavigate();
  const state = useRouterState({ select: (router) => router.location.state });

  /* Resolution, the navigate call and its failure state all live in the hook,
     so this stays composition (style §3.2). No cast: the hook's `NavigateLike`
     is narrow enough that TanStack's `navigate` satisfies it structurally,
     which is the point of not importing the router's generic `NavigateFn`. */
  const { onSuccess, stalled, retry } = usePostLoginNavigation(navigate);

  /* The expected navigation silently did not happen, so the user is left on a
     page whose primary action already succeeded. Move focus to the only
     affordance rather than making a keyboard or screen-reader user hunt for it.
     A stable callback ref fires once when the stall block mounts; an inline
     arrow would re-run on every render and keep stealing focus. */
  const focusOnMount = useCallback((node: HTMLButtonElement | null) => {
    node?.focus();
  }, []);

  const returnTo = readReturnToState(state);

  /*
   * `passkeyMode` comes from the boot config, never a source literal (AD-15).
   * The hard-coded auto ceremony mode that used to sit here was the defect: it
   * shipped bootstrap handle fields to production, where bootstrap is closed,
   * and no deploy-time value could override it without a code change.
   */
  return (
    <>
      <LoginPanel
        config={config}
        passkeyMode={config.passkeyMode}
        returnTo={returnTo}
        onSuccess={onSuccess}
      />
      {stalled !== null && (
        <div className={styles['stall']}>
          {/*
           * `role="alert"` rather than `role="status"`, matching route-guard:
           * terminal and actionable, not progress. The copy names the split
           * state exactly — claiming sign-in failed would contradict a session
           * the server has already issued.
           */}
          <p className={styles['message']} role="alert">
            Signed in, but the page did not open.
          </p>
          <button type="button" className={styles['continue']} onClick={retry} ref={focusOnMount}>
            Continue
          </button>
        </div>
      )}
    </>
  );
}
